import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import Loading from '../../../components/Loading';
import ReactModal from '../../../components/Modal';
import AiTrackingAdd from '../create';
import { STEP_PROMPTS, TOTAL_STEPS } from './state';

const PREFIX = 'ai_tracking.onboarding';

/**
 * Platforms a first project starts on.
 *
 * The two that between them cover most of what people mean by "AI search": the answers Google
 * puts above its own results, and the assistant most people have actually used. Everything else
 * is a platform the user can add once they know what they are looking at, and each one costs a
 * credit per prompt per run, so starting with all five would spend a new account's balance on a
 * choice they never made.
 */
export const DEFAULT_PLATFORMS = ['google_ai_overview', 'chat_gpt'];

/**
 * How many prompts this step accepts.
 *
 * Setup is meant to get tracking running, not to be the sitting in which the whole prompt list
 * gets written. A handful is enough to see real results on the first run, costs little of a new
 * account's credits, and leaves the deciding for later -- the prompt manager takes any number,
 * and is where someone who already knows what they want will add the rest.
 */
export const MAX_PROMPTS = 5;

/**
 * Step 3: the prompts the project will actually track, written by hand.
 *
 * This is the same add-prompts form the prompt generator and the prompt manager open, so what
 * the user learns here is what they will use from now on. Onboarding only arrives with the
 * answers it already has -- the project, its market, weekly frequency and a sensible pair of
 * platforms -- leaving the one question it cannot answer for them: what they want to ask about.
 *
 * Nothing is generated. The person setting up a project knows their own prompts, and a
 * generation they did not ask for spends credits and hands them a list they then have to read
 * and correct. Suggestions remain a step away on the prompt generator page, which the done
 * screen leads to.
 */
class StepPrompts extends Component {
    constructor() {
        super();

        this.onChangeLoading = this.onChangeLoading.bind(this);
        this.onChangeCanSubmit = this.onChangeCanSubmit.bind(this);
        this.onSubmitForm = this.onSubmitForm.bind(this);

        this.submitButtonRef = React.createRef(null);
        this.modalBodyRef = React.createRef(null);

        this.state = {
            adding: false,
            // The form starts incomplete (an empty prompt box), and reports back through
            // onChangeCanSubmit as soon as it is submittable.
            canSubmit: false,
        };
    }

    onChangeLoading(adding) {
        this.setState({ adding });
    }

    onChangeCanSubmit(canSubmit) {
        this.setState({ canSubmit });
    }

    /** The form's own submit button is hidden in modal mode; the footer drives it. */
    onSubmitForm() {
        this.submitButtonRef.current?.click();
    }

    render() {
        const { t, project, isOpen, onClose } = this.props;
        const { adding, canSubmit } = this.state;

        return (
            <ReactModal
                modalState={isOpen}
                className="ai-tracking-modal onboarding-step onboarding-prompts-step ai-tracking-edit-keyword-container"
                onClose={adding ? () => {} : onClose}
            >
                <h3>
                    {t(`${PREFIX}.step_header`, {
                        current: STEP_PROMPTS,
                        total: TOTAL_STEPS,
                        name: t(`${PREFIX}.steps.prompts`),
                    })}
                </h3>
                <p className="as-page-subtitle">
                    {t(`${PREFIX}.prompts.manual_subtitle`, { count: MAX_PROMPTS })}
                </p>
                <div
                    ref={this.modalBodyRef}
                    className="ai-tracking-modal-body ai-tracking-edit-keyword-container-body"
                >
                    <AiTrackingAdd
                        defaultSelectedProject={project?.id}
                        defaultPlatforms={DEFAULT_PLATFORMS}
                        maxPrompts={MAX_PROMPTS}
                        submitButtonRef={this.submitButtonRef}
                        onChangeLoading={this.onChangeLoading}
                        onChangeCanSubmit={this.onChangeCanSubmit}
                        onAdded={this.props.onAdded}
                        isModal
                        modalBodyRef={this.modalBodyRef}
                    />
                </div>
                <div className="ai-tracking-modal-footer">
                    <button
                        type="button"
                        className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                        disabled={adding || !canSubmit}
                        onClick={this.onSubmitForm}
                    >
                        {adding && <Loading onlyPreloader />}
                        {t(`${PREFIX}.prompts.start_tracking`)}
                    </button>
                    <button
                        type="button"
                        className="button button-outline button-primary width-sm btn"
                        disabled={adding}
                        onClick={onClose}
                    >
                        {t(`${PREFIX}.finish_later`)}
                    </button>
                </div>
            </ReactModal>
        );
    }
}

export default withTranslation()(StepPrompts);
